import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Typography
} from '@mui/material';
import { APIHOST } from '../../common';
import { Hotel } from '../../types/hotel';
import { OrderRoom } from '../../types/orderRoom';

interface RoomStats {
  totalBookings?: number;
}

interface PaymentCardInfo {
  NameOnCard?: string;
  Type?: string;
  Number?: string;
  ExpirationDate?: {
    Month?: string;
    Year?: string;
  };
  CVV?: string;
  ZipCode?: string;
}

interface CreditCardInfoResponse {
  data: PaymentCardInfo;
  success: boolean;
}

interface ExportBookingsProps {
  open: boolean;
  onClose: () => void;
  hotelId: string;
  orders: OrderRoom[];
}

export default function ExportBookings({
  open,
  onClose,
  hotelId,
  orders
}: ExportBookingsProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [bookingStats, setBookingStats] = useState<RoomStats[]>([]);
  const [paymentCardByOrderId, setPaymentCardByOrderId] = useState<Record<string, PaymentCardInfo>>({});
  const [paymentCardsLoading, setPaymentCardsLoading] = useState(false);
  const [paymentCardsProcessedCount, setPaymentCardsProcessedCount] = useState(0);
  const [hasStartedExport, setHasStartedExport] = useState(false);
  const [includePaymentCardInfo, setIncludePaymentCardInfo] = useState(true);
  const [includeCVVInfo, setIncludeCVVInfo] = useState(false);

  const uniqueOrderIds = useMemo(() => {
    return Array.from(new Set(
      orders
        .map((room) => room.order?.documentId)
        .filter((id): id is string => Boolean(id))
    ));
  }, [orders]);

  useEffect(() => {
    const load = async () => {
      if (!open || !hotelId) return;

      try {
        setLoading(true);
        setError('');
        setPaymentCardByOrderId({});
        setPaymentCardsProcessedCount(0);
        const token = localStorage.getItem('authToken');

        const [hotelResponse, bookingStatsResponse] = await Promise.all([
          fetch(`${APIHOST}/api/hotel-admin/hotels/${hotelId}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }),
          fetch(`${APIHOST}/api/hotel-admin/hotels/${hotelId}/booking-stats`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          })
        ]);

        if (!hotelResponse.ok) {
          throw new Error(`Failed to load hotel: ${hotelResponse.statusText}`);
        }

        if (!bookingStatsResponse.ok) {
          throw new Error(`Failed to load booking stats: ${bookingStatsResponse.statusText}`);
        }

        const hotelPayload = await hotelResponse.json();
        const bookingStatsPayload = await bookingStatsResponse.json();

        const statsData = bookingStatsPayload?.data ?? bookingStatsPayload;
        setHotel(hotelPayload?.data ?? null);
        setBookingStats(Array.isArray(statsData) ? statsData : []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load export details');
        setHotel(null);
        setBookingStats([]);
        setPaymentCardByOrderId({});
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, hotelId]);

  const totalBookings = useMemo(() => {
    return bookingStats.reduce((sum, row) => sum + (row.totalBookings || 0), 0);
  }, [bookingStats]);

  const paymentCardsLoadedCount = useMemo(() => {
    return Math.min(paymentCardsProcessedCount, uniqueOrderIds.length);
  }, [paymentCardsProcessedCount, uniqueOrderIds.length]);

  const paymentCardsLoadPercent = useMemo(() => {
    if (uniqueOrderIds.length === 0) return 100;
    return Math.round((paymentCardsLoadedCount / uniqueOrderIds.length) * 100);
  }, [paymentCardsLoadedCount, uniqueOrderIds.length]);

  const loadPaymentCards = async (includeCVVInfo: boolean): Promise<Record<string, PaymentCardInfo>> => {
    const token = localStorage.getItem('authToken');
    const paymentCardMap: Record<string, PaymentCardInfo> = {};

    setPaymentCardsLoading(true);
    setPaymentCardsProcessedCount(0);
    setPaymentCardByOrderId({});

    try {
      await Promise.all(
        uniqueOrderIds.map(async (id) => {
          try {
            const paymentCardUrl = `${APIHOST}/api/orders/${id}/paymentCard${includeCVVInfo ? '?retrieveCVV=yes' : ''}`;
            const response = await fetch(paymentCardUrl, {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const cardData = await response.json() as CreditCardInfoResponse;
              paymentCardMap[id] = cardData.data;
              setPaymentCardByOrderId((prev) => ({
                ...prev,
                [id]: cardData.data
              }));
            }
          } finally {
            setPaymentCardsProcessedCount((prev) => prev + 1);
          }
        })
      );

      return paymentCardMap;
    } finally {
      setPaymentCardsLoading(false);
    }
  };

  const exportToCSV = (
    includePaymentCardInfo: boolean,
    includeCVVInfo: boolean,
    paymentCards: Record<string, PaymentCardInfo> = paymentCardByOrderId
  ) => {
    if (orders.length === 0) {
      return;
    }

    const paymentCardHeaders = [
      'CCNameOnCard',
      'CCNumber',
      'CCType',
      'CCExpirationMonth',
      'CCExpirationYear',
      ...(includeCVVInfo ? ['CCCVV'] : []),
      'CCZipCode'
    ];

    const headers = [
      'RoomConfirmation',
      'HotelReservationConfirmation',
      'OrderID',
      'ContactLastName',
      'ContactFirstName',
      'ContactEmail',
      'ContactPhone',
      'BillingName',
      'BillingAddress1',
      'BillingAddress2',
      'BillingCity',
      'BillingState',
      'BillingPostalCode',
      'BillingCountry',
      'Room Type',
      'Room Name',
      'Arrival Date',
      'Departure Date',
      'Payment Method',
      ...(includePaymentCardInfo ? paymentCardHeaders : []),
      'Occupant1LastName',
      'Occupant1FirstName',
      'Occupant2LastName',
      'Occupant2FirstName',
      'Occupant3LastName',
      'Occupant3FirstName',
      'Occupant4LastName',
      'Occupant4FirstName',
      'Occupant5LastName',
      'Occupant5FirstName',
      'Order URL',
      'Notes For Hotel'
    ];

    const rows = orders.map((room) => {
      const occupants = Array.isArray(room.order_room_occupants) ? room.order_room_occupants : [];
      const paymentCard = paymentCards[room.order?.documentId || ''];

      const occupant = (index: number) => {
        const currentOccupant = occupants[index];
        if (!currentOccupant) return { lastName: '', firstName: '' };
        return currentOccupant;
      };

      return [
        room.confirmation || 'N/A',
        '',
        room.order?.confirmation || 'N/A',
        room.order?.contactLastName,
        room.order?.contactFirstName,
        room.order?.contactEmail,
        room.order?.contactCell,
        room.order?.billingAddressee,
        room.order?.billingStreet1,
        room.order?.billingStreet2,
        room.order?.billingCity,
        room.order?.billingState,
        room.order?.billingZip,
        room.order?.billingCountry,
        room.type || 'N/A',
        room.conference_hotel_room?.name || 'N/A',
        room.arrivalDate,
        room.departureDate,
        room.order?.paymentMethod || 'credit-card',
        ...(includePaymentCardInfo ? [
          paymentCard?.NameOnCard || '',
          paymentCard?.Number || '',
          paymentCard?.Type || '',
          paymentCard?.ExpirationDate?.Month || '',
          paymentCard?.ExpirationDate?.Year || '',
          ...(includeCVVInfo ? [paymentCard?.CVV || ''] : []),
          paymentCard?.ZipCode || ''
        ] : []),
        occupant(0).lastName,
        occupant(0).firstName,
        occupant(1).lastName,
        occupant(1).firstName,
        occupant(2).lastName,
        occupant(2).firstName,
        occupant(3).lastName,
        occupant(3).firstName,
        occupant(4).lastName,
        occupant(4).firstName,
        room.order?.notesForHotel || '',
        `https://citywidehousingsolutions.com/hotel-admin/orders/${room.order?.documentId || ''}`
      ];
    });

    const escapeCSV = (field: string | undefined | null) => {
      if (!field) return '';
      if (field.includes(',') || field.includes('"') || field.includes('\n')) {
        return `"${field.replace(/"/g, '""')}"`;
      }
      return field;
    };

    const csvContent = [
      headers.map(escapeCSV).join(','),
      ...rows.map((row) => row.map((cell) => escapeCSV(cell?.toString())).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${hotel?.name || 'hotel'}-rooms-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    onClose();
  };

  const handleExport = async () => {
    setHasStartedExport(true);

    if (!includePaymentCardInfo) {
      exportToCSV(false, false);
      return;
    }

    try {
      const paymentCards = await loadPaymentCards(includeCVVInfo);
      exportToCSV(true, includeCVVInfo, paymentCards);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payment card info');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export Bookings</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box>
            <Typography variant="h6">{hotel?.name || 'Unknown Hotel'}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Total bookings: {totalBookings}
            </Typography>
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Checkbox
                  checked={includePaymentCardInfo}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setIncludePaymentCardInfo(checked);
                    if (!checked) {
                      setIncludeCVVInfo(false);
                    }
                  }}
                />
              }
              label="Include payment card information in export"
            />
             {includePaymentCardInfo && <Alert severity="info" sx={{ mt: 2 }}>
              Be cautious when including payment card information, especially CVV. Ensure that the exported data is stored securely and only shared with authorized personnel.
            </Alert>}
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeCVVInfo}
                  onChange={(event) => setIncludeCVVInfo(event.target.checked)}
                  disabled={!includePaymentCardInfo}
                />
              }
              label="Include CVV in export"
            />
            {includeCVVInfo &&<Alert severity="warning" sx={{ mt: 2 }}>
              CVV is restricted to 5 retrievals per card. After 5 retrivals, you will need to contact support to gain access to CVV. Use with caution.
            </Alert>}
            {includePaymentCardInfo && hasStartedExport && paymentCardsLoading && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Payment cards loaded: {paymentCardsLoadPercent}% ({paymentCardsLoadedCount}/{uniqueOrderIds.length})
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={paymentCardsLoadPercent}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Loading payment cards...
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={loading || paymentCardsLoading || Boolean(error)}
        >
          Export CSV
        </Button>
      </DialogActions>
    </Dialog>
  );
}
